/**
 * The Google tag runtime (ui#256) — React-free, browser-guarded, and the ONLY place this app touches
 * `window.dataLayer`, injects a Google script, or names a Google cookie.
 *
 * Basic Consent Mode: {@link startGoogleTagging} refuses to load anything until a granted purpose needs a
 * tag, so a visitor who has not decided — or who refused — causes zero requests to Google, not even a
 * cookieless ping. Once loading is justified the order is fixed and important: denied defaults, then the
 * redaction settings, then the visitor's actual choice, and only then the tag script and its `config`.
 *
 * ── One tag, and it sends no events (kitecorp/transferify-api-service#1929) ──────────────────────
 * The GA4 half is gone from the browser. Analytics events are reported to our own api, which forwards
 * them to GA4 server-side, so what remains here is the Google Ads tag and its `config` alone: a
 * remarketing page hit, and nothing else. Nothing in this app may push an event to `gtag` any more —
 * there is deliberately no function here that can.
 *
 * SSG-safe: nothing here runs at module scope, and every export no-ops outside a browser.
 */

import type {ConsentState} from "consent/consentStore";
import {expireCookiesMatching} from "./cookieExpiry";
import {CONSENT_DEFAULTS, consentModeSignals, googleTaggingAllowed, REDACTION_SETTINGS} from "./googleConsentMode";

/** The tag id this deployment is configured with; absent on dev, staging and an unconfigured prod. */
export interface GoogleTagConfig {
	/** Google Ads conversion id (`AW-…`). */
	adsId: string | null;
}

type GtagFunction = (...args: unknown[]) => void;

interface TaggingWindow extends Window {
	dataLayer?: unknown[];
	gtag?: GtagFunction;
}

const TAG_SCRIPT_ORIGIN = "https://www.googletagmanager.com/gtag/js?id=";

/** The id a running tag configured, or null while nothing is running. Also the single-flight latch. */
let running: string | null = null;

function taggingWindow(): TaggingWindow | null {
	return typeof window === "undefined" ? null : (window as TaggingWindow);
}

/**
 * The canonical `gtag`, installed exactly as Google documents it. It pushes the ARGUMENTS object rather
 * than an array on purpose — that is the shape gtag.js reads commands in.
 */
function gtagOf(scope: TaggingWindow): GtagFunction {
	if (!scope.dataLayer) {
		scope.dataLayer = [];
	}
	if (!scope.gtag) {
		scope.gtag = function gtag() {
			// eslint-disable-next-line prefer-rest-params
			scope.dataLayer!.push(arguments);
		} as GtagFunction;
	}
	return scope.gtag;
}

/**
 * Every command pushed to the dataLayer so far, as plain arrays.
 *
 * This is a read of what the page has ALREADY sent — the same thing a browser devtools inspection shows —
 * and it is what the unit tests and the Playwright battery assert against, so "no Google traffic before
 * consent" is provable rather than asserted by inspection of the source.
 */
export function googleTagCommands(): unknown[][] {
	const scope = taggingWindow();
	return (scope?.dataLayer ?? []).map((entry) => Array.from(entry as ArrayLike<unknown>));
}

/**
 * Load and configure the Google tags iff a granted purpose justifies it and an id is configured.
 *
 * Single-flight: repeated calls (banner mount, consent change, route change) never re-declare defaults or
 * re-inject the script — an unlatched second call would push a `consent default` AFTER the visitor's
 * update, silently resetting their granted state back to denied.
 *
 * @returns whether a tag is running after this call
 */
export function startGoogleTagging(config: GoogleTagConfig, consent: ConsentState): boolean {
	const scope = taggingWindow();
	if (scope === null) {
		return false;
	}
	if (running !== null) {
		return true;
	}
	const id = config.adsId;
	if (id === null || !googleTaggingAllowed(consent)) {
		return false;
	}

	const gtag = gtagOf(scope);
	gtag("consent", "default", {...CONSENT_DEFAULTS});
	gtag("set", {...REDACTION_SETTINGS});
	gtag("consent", "update", consentModeSignals(consent));
	gtag("js", new Date());
	gtag("config", id);

	// A withdrawal cannot unload gtag.js, so a re-grant in the same session finds the script already there;
	// injecting a second copy would re-run the loader against the same dataLayer for no benefit.
	const source = `${TAG_SCRIPT_ORIGIN}${id}`;
	if (scope.document.querySelector(`script[data-google-tag][src="${source}"]`) === null) {
		const script = scope.document.createElement("script");
		script.async = true;
		script.src = source;
		script.setAttribute("data-google-tag", "");
		scope.document.head.appendChild(script);
	}
	running = id;
	return true;
}

/** Apply a consent change to a RUNNING tag. A tag that never loaded has nothing to update. */
export function updateGoogleConsent(consent: ConsentState): void {
	const scope = taggingWindow();
	if (scope === null || running === null) {
		return;
	}
	gtagOf(scope)("consent", "update", consentModeSignals(consent));
}

/**
 * Withdrawal: stop collecting NOW rather than "next page load".
 *
 * The script cannot be unloaded, so both levers are pulled together — a denied consent update and removal
 * of the identifiers already written — because either alone leaves the visitor still measurable: the
 * update alone leaves the cookies behind, and the cookies alone leave the tag able to recognise them.
 *
 * `ga-disable-G-…` used to be a third lever here. It is GA4's own per-measurement kill switch and there is
 * no GA4 measurement id in this build any more (kitecorp/transferify-api-service#1929), so setting it
 * would name nothing: a flag read by a tag that is not on the page is not a withdrawal, it is a line that
 * makes one look thorough. The server-side half honours the same withdrawal by receiving no further event.
 */
export function stopGoogleTagging(): void {
	const scope = taggingWindow();
	if (scope === null || running === null) {
		return;
	}
	gtagOf(scope)("consent", "update", {...CONSENT_DEFAULTS});
	expireCookiesMatching(scope.document, GOOGLE_COOKIE_NAME_PATTERN);
	running = null;
}

/**
 * The cookie names the withdrawal path below deletes, as a PREFIX rule rather than a list: `_ga`/`_ga_*`
 * (GA4 client + session), `_gcl_*` (ad click) and `_gac_*` (campaign). A list would be a fact about
 * somebody else's script and would go stale with nothing noticing.
 *
 * Exported because the Cookie Policy QUOTES these names as published legal copy in three locales, and
 * nothing used to tie the two together (ui#1349): `src/i18n/cookiePolicyIdentifiers.test.ts` holds every
 * Google cookie name the policy prints to this pattern, so narrowing it fails a test instead of silently
 * making a published document wrong.
 *
 * The `_ga` half stays even though this build loads no GA4 tag (kitecorp/transferify-api-service#1929).
 * Two reasons, and either alone is enough: the published policy still names those cookies, and a
 * returning visitor's browser still holds the ones an earlier build wrote — a withdrawal that left them
 * in place would be a withdrawal in name only. Narrowing the pattern is the cookie-policy ticket's job,
 * not this one's.
 */
export const GOOGLE_COOKIE_NAME_PATTERN = /^_(ga|gcl|gac)/;

/**
 * The two halves of {@link GOOGLE_COOKIE_NAME_PATTERN}, split by the PURPOSE that justifies each.
 *
 * Withdrawing one purpose must not delete the identifiers the visitor is still consenting to: a reader
 * who drops analytics but keeps advertising has agreed to ad measurement, and taking `_gcl_*` away
 * would silently end the measurement they permitted. The union above stays the published-copy rule,
 * because the Cookie Policy names all three families in one sentence.
 */
export const GOOGLE_ANALYTICS_COOKIE_NAME_PATTERN = /^_ga/;
export const GOOGLE_ADVERTISING_COOKIE_NAME_PATTERN = /^_(gcl|gac)/;

/**
 * Delete the Google cookies of ONE withdrawn purpose, whether or not a tag ran in this page load.
 *
 * Independent of {@link running}, and that independence is the whole point. The clearing used to be
 * reachable only through {@link stopGoogleTagging}, which returns early when nothing is up. Since
 * kitecorp/transferify-api-service#1929 the tag loads only on the ADVERTISING grant, so a returning
 * browser holding `_ga` from an earlier build — the exact reader the Cookie Policy's "withdrawing
 * consent deletes them" sentence addresses — withdrew and kept them. A withdrawal has to act on what
 * the DEVICE holds, not on what this page load happened to start.
 */
export function clearGoogleAnalyticsCookies(): void {
	clearWithdrawnCookies(GOOGLE_ANALYTICS_COOKIE_NAME_PATTERN);
}

/** The advertising half of {@link clearGoogleAnalyticsCookies}, for the same reason. */
export function clearGoogleAdvertisingCookies(): void {
	clearWithdrawnCookies(GOOGLE_ADVERTISING_COOKIE_NAME_PATTERN);
}

function clearWithdrawnCookies(pattern: RegExp): void {
	const scope = taggingWindow();
	if (scope === null) {
		return;
	}
	expireCookiesMatching(scope.document, pattern);
}
